<template>
    <div v-if="PCM.id" class="pcm-documento-wrapper">
        <article class="pcm-documento" aria-label="Formulário PCM">
            <header class="pcm-doc-cabecalho">
                <div class="pcm-doc-cabecalho__marca">
                    <img :src="logoEmpresa" alt="Logotipo" class="pcm-doc-logo" width="168" height="48" />
                </div>
                <div class="pcm-doc-cabecalho__texto">
                    <h1 class="pcm-doc-titulo-principal">Proposta de Projeto
                        <i v-if="PCM.tipo === 'Projeto' && PCM.id !== 'criar'" class="pcm-doc-icone-pdf"
                            :class="baixandoPdf ? 'bi-hourglass-split' : 'bi-file-earmark-pdf'" role="button"
                            tabindex="0" :title="baixandoPdf ? 'Baixando PDF...' : 'Baixar PDF'"
                            aria-label="Baixar PDF da proposta" @click="baixarPdf" @keyup.enter="baixarPdf"></i>
                    </h1>
                    <p class="pcm-doc-titulo-secundario">documento para registro e análise da iniciativa</p>
                </div>
                <div class="pcm-doc-cabecalho__meta" v-if="PCM.id !== 'criar'">
                    <div class="pcm-doc-meta-bloco">
                        <span class="pcm-doc-meta-rotulo">Código</span>
                        <span class="pcm-doc-meta-valor">{{ PCM.codigo || '—' }}</span>
                    </div>
                    <div class="pcm-doc-meta-bloco" v-if="PCM.status">
                        <span class="pcm-doc-meta-rotulo">Situação</span>
                        <span class="pcm-doc-meta-valor pcm-doc-meta-valor--status">{{ PCM.status }}</span>
                    </div>
                </div>
                <div class="pcm-doc-cabecalho__meta pcm-doc-cabecalho__meta--rascunho" v-else>
                    <span class="pcm-doc-rascunho">Novo cadastro</span>
                </div>
            </header>
            <div class="pcm-doc-traco-duplo" aria-hidden="true" />
            <div class="pcm-doc-corpo">
                <div v-if="PCM.id === 'criar' && rascunhoPendente" class="pcm-rascunho-aviso" role="status">
                    <div class="pcm-rascunho-aviso__texto">
                        <strong>Rascunho encontrado.</strong>
                        Há um preenchimento não enviado, salvo em
                        {{ formatarDataHoraRascunho(rascunhoPendente.salvoEm) }}.
                        <span v-if="rascunhoPendente.anexosPendentes">
                            Anexos não são guardados no rascunho e precisarão ser adicionados novamente.
                        </span>
                    </div>
                    <div class="pcm-rascunho-aviso__acoes">
                        <button type="button" class="acao-secundaria" @click="descartarRascunho">Descartar</button>
                        <button type="button" @click="restaurarRascunho">Restaurar rascunho</button>
                    </div>
                </div>
                <h2 class="pcm-doc-secao-principal">Dados cadastrais</h2>
                <fieldset class="pcm-doc-fieldset bloco2 margem">
                    <div class="grid-4">
                        <div class="col-2 pcm-ai-field" :class="aiFieldClass('nome')">
                            <h3>Nome do {{ PCM.tipo }}
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('nome')"></i>
                            </h3>
                            <input @focusout="atualizarPCM('nome', PCM.nome)" :required="!PCM.nome" v-model="PCM.nome"
                                type="text" :readonly="bloquearEdicaoCampos" @input="marcarCampoManual('nome')" />
                        </div>
                        <div class="pcm-ai-field" :class="aiFieldClass('nivel')">
                            <label>Nível
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('nivel')"></i>
                            </label>
                            <select v-model="PCM.nivel" @change="atualizarPCM('nivel', PCM.nivel)"
                                :disabled="bloquearEdicaoCampos" :required="!PCM.nivel">
                                <option :hidden="PCM.tipo == 'PAP'">Estratégico</option>
                                <option>Operacional</option>
                            </select>
                        </div>
                        <div>
                            <label>Tipo</label>
                            <select v-model="PCM.tipo" :required="!PCM.tipo" @change="atualizarPCM('tipo', PCM.tipo)"
                                :disabled="true">
                                <option>Programa</option>
                                <option>Projeto</option>
                                <option :hidden="nivel == 'Estratégico'">PAP</option>
                            </select>
                        </div>
                        <div class="pcm-ai-field" :class="aiFieldClass('finalidade')">
                            <label>Finalidade
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('finalidade')"></i>
                            </label>
                            <select v-model="PCM.finalidade" :required="!PCM.finalidade"
                                @change="atualizarPCM('finalidade', PCM.finalidade)" :disabled="bloquearEdicaoCampos">
                                <option>Criação</option>
                                <option>Mudança</option>
                            </select>
                        </div>
                        <div class="pcm-ai-field" :class="aiFieldClass('area')">
                            <label>Área
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('area')"></i>
                            </label>
                            <select v-model="PCM.area" :required="!PCM.area" @change="atualizarPCM('area', PCM.area)"
                                :disabled="bloquearEdicaoCampos">
                                <option>Processo</option>
                                <option>Produto</option>
                            </select>
                        </div>
                        <div class="pcm-ai-field" :class="aiFieldClass('setor_id')">
                            <label>Setor aprovador
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('setor_id')"></i>
                            </label>
                            <select v-model="PCM.setor_id" :required="!PCM.setor_id" :disabled="bloquearEdicaoCampos"
                                @change="atualizarPCM('setor_id', PCM.setor_id)" style="text-align: left;">
                                <option :hidden="PCM.nivel == 'Estratégico'" v-for="item in setores" :key="item.id"
                                    :value="item.id"
                                    :style="{ 'font-weight': (item.nivel_hierarquico <= 2) ? 'bold' : '0' }">
                                    <span v-if="item.nivel_hierarquico == 3" style="color: red;"> &nbsp;&nbsp; </span>
                                    {{ item.nivel_hierarquico == 3 ? "-" : '' }} {{ item.nome }}
                                </option>
                                <option :hidden="PCM.nivel !== 'Estratégico'" :value="7"> Diretoria </option>
                            </select>
                        </div>
                        <div class="pcm-ai-field" :class="aiFieldClass('dtInicio')">
                            <label>Data de abertura
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('dtInicio')"></i>
                            </label>
                            <input v-model="PCM.dtInicio" type="date" @focusout="atualizarPCM('dtInicio', PCM.dtInicio)"
                                :readonly="bloquearEdicaoCampos" :required="!PCM.dtInicio" />
                        </div>
                        <div v-if="PCM.tipo == 'Programa'">
                            <label>Descrição do Problema
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('descricao_problema')"></i>
                            </label>
                            <textarea :title="PCM.descricao_problema" v-model="PCM.descricao_problema"
                                :required="!PCM.descricao_problema" :readonly="bloquearEdicaoCampos"
                                @focusout="atualizarPCM('descricao_problema', PCM.descricao_problema)"></textarea>
                        </div>
                        <div v-if="PCM.tipo == 'Programa'">
                            <label>Possível solução
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('possivel_solucao')"></i>
                            </label>
                            <textarea :title="PCM.possivel_solucao" v-model="PCM.possivel_solucao"
                                :required="!PCM.possivel_solucao" :readonly="bloquearEdicaoCampos"
                                @focusout="atualizarPCM('possivel_solucao', PCM.possivel_solucao)"></textarea>
                        </div>
                        <div class="col-4 pcm-ai-field" :class="aiFieldClass('objetivo_geral')">
                            <h3>Objetivo Geral
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('objetivo_geral')"></i>
                            </h3>
                            <textarea :title="PCM.objetivo_geral" v-model="PCM.objetivo_geral"
                                :required="!PCM.objetivo_geral" :readonly="bloquearEdicaoCampos"
                                @input="marcarCampoManual('objetivo_geral')"
                                @focusout="atualizarPCM('objetivo_geral', PCM.objetivo_geral)"></textarea>
                        </div>
                        <div class="col-4 pcm-ai-field" :class="aiFieldClass('descricao_problema')">
                            <h3>Problema a ser solucionado
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('descricao_problema')"></i>
                            </h3>
                            <textarea :title="PCM.descricao_problema" v-model="PCM.descricao_problema"
                                :required="!PCM.descricao_problema" :readonly="bloquearEdicaoCampos"
                                @input="marcarCampoManual('descricao_problema')"
                                @focusout="atualizarPCM('descricao_problema', PCM.descricao_problema)"></textarea>
                        </div>
                        <div class="col-4 pcm-ai-field" :class="aiFieldClass('objetivos_estrategicos')">
                            <h3>Objetivos Estratégicos Impactados Pelo Projeto
                                <i v-if="!somenteLeitura" class="bi-gear pcm-icone-pedido"
                                    title="Gerenciar objetivos estratégicos"
                                    @click="mostrarCrudObjetivos = true"></i>
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar edição dos objetivos estratégicos"
                                    @click="solicitarEdicaoObjetivosComEstadoAtual"></i>
                            </h3>
                            <div class="grid-4">
                                <div v-for="i, index in PCM.objetivos_estrategicos ? PCM.objetivos_estrategicos : objetivos"
                                    :key="index">
                                    <label style="margin: 0;">
                                        <input type="checkbox" v-model="i.ativo" :true-value="1" :false-value="0"
                                            :disabled="bloquearEdicaoCampos && !podeSolicitarEdicao"
                                            @change="onChangeObjetivoEstrategico(i)"> {{
                                                i.nome }} </label>
                                </div>
                            </div>
                            <div v-if="bloquearEdicaoCampos && temAlteracoesObjetivosPendentes"
                                class="pcm-objetivos-acoes">
                                <button type="button" class="acao-secundaria" @click="salvarPedidoObjetivos">
                                    Salvar Pedido de edição
                                </button>
                            </div>
                        </div>
                        <div v-if="PCM.tipo !== 'Programa'" class="col-4 pcm-ai-field"
                            :class="aiFieldClass('escopo')">
                            <h3>Escopo
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('escopo')"></i>
                            </h3>
                            <textarea :title="PCM.escopo" v-model="PCM.escopo" :required="!PCM.escopo"
                                :readonly="bloquearEdicaoCampos"
                                @input="marcarCampoManual('escopo')"
                                @focusout="atualizarPCM('escopo', PCM.escopo)"></textarea>
                        </div>
                        <div v-if="PCM.tipo !== 'Programa'" class="margem bloco2 col-4">
                            <h3>Entregas</h3>
                            <div class="pcm-resultados-lista">
                                <article v-for="(res, idx) in (PCM.resultados || [])"
                                    :key="res.id ?? res.local_id" class="pcm-resultado-card"
                                    :class="['pcm-ai-field', aiFieldClass(`resultados.${res.local_id}`)]">
                                    <header class="pcm-resultado-card__cabecalho">
                                        <span class="pcm-resultado-card__indice">{{ idx + 1 }}</span>
                                        <div class="pcm-resultado-card__nome">
                                            <label>Nome</label>
                                            <div class="pcm-celula-campo">
                                                <input v-model="res.nome" type="text"
                                                    placeholder="Título curto e identificável da entrega"
                                                    @input="marcarResultadoManual(res, 'nome')"
                                                    :readonly="!podeEditarResultadosLocalmente" />
                                                <button v-if="podeSolicitarPedidoCampoResultado(res)" type="button"
                                                    class="pcm-btn-pedido" title="Solicitar alteração"
                                                    @click="abrirPedidoCampoResultado(res, idx, 'nome')">
                                                    <i class="bi-pencil-square" aria-hidden="true"></i>
                                                </button>
                                            </div>
                                        </div>
                                        <div v-if="PCM.id == 'criar' || podeEditarResultadosLocalmente || podeSolicitarPedidoResultado"
                                            class="pcm-resultado-card__acoes">
                                            <button v-if="podeEditarResultadosLocalmente" type="button"
                                                class="chip bg-erro" @click="removerResultado(idx)">Excluir</button>
                                            <button v-else-if="podeSolicitarPedidoCampoResultado(res)" type="button"
                                                class="chip bg-erro" title="Solicitar exclusão"
                                                @click="abrirPedidoExcluirResultado(res, idx)">Excluir</button>
                                        </div>
                                    </header>

                                    <div class="pcm-resultado-card__conteudo">
                                        <div>
                                            <label>Especificação da entrega</label>
                                            <div class="pcm-celula-campo">
                                                <textarea v-model="res.descricao" class="pcm-textarea" rows="3"
                                                    placeholder="Qual estado concreto deverá existir ao final?"
                                                    @input="marcarResultadoManual(res, 'descricao')"
                                                    :readonly="!podeEditarResultadosLocalmente"></textarea>
                                                <button v-if="podeSolicitarPedidoCampoResultado(res)" type="button"
                                                    class="pcm-btn-pedido" title="Solicitar alteração"
                                                    @click="abrirPedidoCampoResultado(res, idx, 'descricao')">
                                                    <i class="bi-pencil-square" aria-hidden="true"></i>
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label>Evidência</label>
                                            <div class="pcm-celula-campo">
                                                <textarea v-model="res.evidencias" class="pcm-textarea" rows="3"
                                                    placeholder="Como comprovamos que a entrega foi alcançada?"
                                                    @input="marcarResultadoManual(res, 'evidencias')"
                                                    :readonly="!podeEditarResultadosLocalmente"></textarea>
                                                <button v-if="podeSolicitarPedidoCampoResultado(res)" type="button"
                                                    class="pcm-btn-pedido" title="Solicitar alteração"
                                                    @click="abrirPedidoCampoResultado(res, idx, 'evidencias')">
                                                    <i class="bi-pencil-square" aria-hidden="true"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="pcm-resultado-card__rodape">
                                        <div>
                                            <label>Data prevista</label>
                                            <div class="pcm-celula-campo">
                                                <input v-model="res.dt_prevista_conclusao" type="date"
                                                    :disabled="!podeEditarResultadosLocalmente"
                                                    @change="onChangeDtPrevistaResultado(res)" />
                                                <button v-if="podeSolicitarPedidoCampoResultado(res)" type="button"
                                                    class="pcm-btn-pedido" title="Solicitar alteração"
                                                    @click="abrirPedidoCampoResultado(res, idx, 'dt_prevista_conclusao')">
                                                    <i class="bi-pencil-square" aria-hidden="true"></i>
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <label>Data de conclusão</label>
                                            <input type="text" readonly
                                                :value="formatarDataBr(res.dt_conclusao, '')" placeholder="—" />
                                        </div>
                                        <div class="pcm-resultado-card__custo">
                                            <label>Custo estimado</label>
                                            <div v-if="podeEditarResultadosLocalmente || isGerente || podeSolicitarPedidoResultado"
                                                class="pcm-celula-campo pcm-celula-campo--custo">
                                                <template v-if="podeEditarResultadosLocalmente">
                                                    <p v-if="res.sem_custo" class="pcm-aviso-sem-custo">
                                                        Esta entrega não terá custo
                                                    </p>
                                                    <input v-else-if="res.cost_state === 'pending'"
                                                        v-model.number="res.custo" type="number" min="0" step="0.01"
                                                        placeholder="Pendente"
                                                        @input="onCustoPendenteInput(res)"
                                                        class="pcm-input-custo" />
                                                    <money3 v-else v-model="res.custo" v-bind="config"
                                                        @blur="marcarResultadoManual(res, 'custo')"
                                                        class="pcm-input-custo"></money3>
                                                    <label class="pcm-check-sem-custo">
                                                        <input type="checkbox" :checked="!!res.sem_custo"
                                                            @change="onToggleSemCustoResultado(res, $event.target.checked)" />
                                                        <span>Sem custo</span>
                                                    </label>
                                                </template>
                                                <template v-else>
                                                    <div v-if="res.sem_custo" class="pcm-aviso-sem-custo">
                                                        Esta entrega não terá custo
                                                    </div>
                                                    <span v-else-if="res.cost_state === 'pending'"
                                                        class="pcm-aviso-sem-custo">
                                                        Pendente
                                                    </span>
                                                    <money3 v-else v-model="res.custo"
                                                        :readonly="true" v-bind="config"
                                                        class="pcm-input-custo"></money3>
                                                    <button v-if="podeSolicitarPedidoCampoResultado(res)" type="button"
                                                        class="pcm-btn-pedido" title="Solicitar alteração do custo"
                                                        @click="abrirPedidoCampoResultado(res, idx, res.sem_custo ? 'sem_custo' : 'custo')">
                                                        <i class="bi-pencil-square" aria-hidden="true"></i>
                                                    </button>
                                                </template>
                                            </div>
                                            <input v-else type="text" value="●●●●●" readonly>
                                        </div>
                                    </div>
                                </article>
                            </div>
                            <div v-if="PCM.id == 'criar' || podeEditarResultadosLocalmente || podeSolicitarPedidoResultado"
                                class="alinha-centro pcm-resultados-acoes">
                                <button type="button" class="acao-secundaria" @click="handleAdicionarResultado">
                                    Adicionar entrega
                                </button>
                            </div>
                        </div>
                    </div>
                    <br>
                    <table v-if="PCM.tipo !== 'Programa'" class="tabela pcm-ai-field"
                        :class="aiFieldClass('impacto_viabilidade')"
                        style="border-bottom: 1px solid var(--cor-separador);">
                        <tbody>
                            <tr>
                                <th style="text-align: left !important;" scope="col">
                                    <h3>Análise de impactos e viabilidade da proposta</h3>
                                </th>
                                <th style="text-align: left !important;" scope="col"></th>
                                <th scope="col">Justificativa</th>
                            </tr>
                            <tr v-for="(item, index) in PCM.impacto_viabilidade ?? impacto_viabilidade" :key="index">
                                <td style="text-align: left !important;">
                                    <div> {{ item.descricao }} </div>
                                </td>
                                <td style="text-align: center; padding: 0; align-content: center;">
                                    <div class="tags" style="justify-content: center;">
                                        <a @click="selecionarImpactoViabilidade(item, true)"
                                            :class="item.concordo == true ? 'ativo' : ''">Sim</a>
                                        <a @click="selecionarImpactoViabilidade(item, false)"
                                            :class="item.concordo == false ? 'ativo' : ''">Não</a>
                                    </div>
                                    <small v-if="item.impact_state || item.impact_reason"
                                        :title="item.impact_reason || item.justificativa"
                                        style="display: block; margin-top: .35rem;">
                                        {{ item.impact_state === 'yes'
                                            ? 'Sim sugerido'
                                            : item.impact_state === 'no'
                                                ? 'Não sugerido'
                                                : 'Pendente de definição' }}
                                        <span v-if="item.impact_reason"
                                            style="display: block; margin-top: .2rem;">
                                            {{ item.impact_reason }}
                                        </span>
                                    </small>
                                </td>
                                <td style="padding: 1rem; width: 30%;">
                                    <textarea v-model="item.justificativa" rows="3" :id="'justificativa' + index"
                                        :title="item.justificativa" :readonly="bloquearEdicaoCampos"
                                        @change="atualizarPCM('impacto_viabilidade', objetoSemIndiceDescricao(item),
                                            `impacto_viabilidade.${item.impactoViabilidade_id}`)"></textarea>
                                </td>
                            </tr>
                            <tr v-if="PCM.nivel != 'Operacional'">
                                <td style="text-align: left !important;">
                                    <div>Estimativa de Custo da Mudança</div>
                                </td>
                                <td style="text-align: left !important; align-content: center;">
                                        <template v-if="podeVerCustoEstimado">
                                            <span v-if="custoTotalEstado === 'pending'"
                                                class="pcm-aviso-sem-custo">Pendente</span>
                                            <money3 v-else :model-value="custoTotalResultados" :readonly="true"
                                                v-bind="config"
                                                title="Soma dos custos previstos das entregas"
                                                style="width: 8rem; border: none; outline: none; font-size: 20px; background-color: transparent !important;">
                                            </money3>
                                        </template>
                                        <input v-else type="text" value="●●●●●" readonly>
                                </td>
                                <td style="padding: 1rem">
                                    <div class="alinha-v">
                                        <label v-if="podeSolicitarEdicao" class="pcm-label-inline">Justificativa do
                                            custo
                                            <i class="bi-pencil-square pcm-icone-pedido" title="Solicitar Edição"
                                                @click="abrirModal('custo_justificativa')"></i>
                                        </label>
                                        <textarea v-model="PCM.custo_justificativa" rows="3"
                                            :title="PCM.custo_justificativa" id="custo_justificativa"
                                            :readonly="bloquearEdicaoCampos"
                                            @change="atualizarPCM('custo_justificativa', PCM.custo_justificativa)"></textarea>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <br>
                    <div class="pcm-ai-field" :class="aiFieldClass('ganho_operacional_esperado')">
                        <h3>Resultado Esperado
                            <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                title="Solicitar Edição" @click="abrirModal('ganho_operacional_esperado')"></i>
                        </h3>
                        <textarea :title="PCM.ganho_operacional_esperado" v-model="PCM.ganho_operacional_esperado"
                            :required="!PCM.ganho_operacional_esperado" :readonly="bloquearEdicaoCampos"
                            @input="marcarCampoManual('ganho_operacional_esperado')"
                            @focusout="atualizarPCM('ganho_operacional_esperado', PCM.ganho_operacional_esperado)"></textarea>
                    </div>
                    <div v-if="PCM.tipo !== 'Programa'" class="pcm-doc-declaracao">
                        <span> Confirmo que o cenário geral do mercado é viável à proposta, pois apresenta condições
                            favoráveis que podem sustentar o sucesso da iniciativa. Além disso, a empresa possui
                            capacidade
                            interna necessária para implementá-la, contando com recursos humanos qualificados e
                            infraestrutura adequada. Com isso, demonstra-se que a implementação da proposta será
                            rentável à
                            empresa, gerando retornos positivos. </span>
                    </div>
                    <br>
                    <div class="margem bloco2">
                        <div class="alinha-centro">
                            <h3>Anexos</h3>
                        </div>
                        <ul v-if="PCM.id == 'criar'" class="lista-anexos">
                            <li v-for="(it, i) in stagedFiles" :key="i">
                                <span class="nome" :title="it.file.name">{{ it.file.name }}</span>
                                <i @click.stop="removerStaged(i)" class="bi-trash" title="Excluir Anexo"></i>
                            </li>
                        </ul>

                        <ul v-else class="lista-anexos">
                            <li v-for="a in (PCM.anexos || [])" :key="a.id">
                                <a class="nome" :title="a.nome_original" :href="storageUrl + a.caminho"
                                    target="_blank">{{
                                        a.nome_original }}</a>
                                <i class="bi-trash" title="Excluir Anexo" @click.stop="excluirAnexo(a.id)" hidden></i>
                            </li>
                            <li v-if="(PCM.anexos || []).length === 0"><i>Nenhum anexo</i></li>
                        </ul>

                        <div class="dropzone" :class="{ dragging: isDragging }" @dragenter="onDragEnter"
                            v-if="PCM.id == 'criar'" @dragover="onDragOver" @dragleave="onDragLeave" @drop="onDrop"
                            @click="abrirFileDialog">
                            <p>Arraste aqui ou clique para selecionar</p>
                            <input ref="fileInput" type="file" multiple hidden @change="handleFileSelect" />
                        </div>
                    </div>
                    <fieldset v-if="PCM.tipo !== 'Programa'" class="grid-2 margem">
                        <div class="pcm-ai-field" :class="aiFieldClass('dtEstimadaFinal')">
                            <label>Data prevista de conclusão
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('dtEstimadaFinal')"></i>
                            </label>
                            <input v-model="PCM.dtEstimadaFinal" type="date" :readonly="bloquearEdicaoCampos"
                                :required="!PCM.dtEstimadaFinal"
                                @change="atualizarPCM('dtEstimadaFinal', PCM.dtEstimadaFinal)" />
                        </div>
                        <div class="pcm-ai-field" :class="aiFieldClass('gerentePCM_id')">
                            <label>Gerente responsável
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('gerentePCM_id')"></i>
                            </label>
                            <select v-model="PCM.gerentePCM_id" :required="!PCM.gerentePCM_id"
                                @change="atualizarPCM('gerentePCM_id', PCM.gerentePCM_id)"
                                :disabled="bloquearEdicaoCampos">
                                <option v-for="item in usuarios" :key="item.id" :value="item.id"> {{ (item.nomeCompleto)
                                    }}
                                </option>
                            </select>
                        </div>
                    </fieldset>
                    <div class="grid-2 margem">
                        <div>
                            <label>Responsável pela solicitação</label>
                            <select disabled v-model="PCM.solicitante_id" :required="!PCM.solicitante_id"
                                @change="atualizarPCM('solicitante_id', PCM.solicitante_id)">
                                <option v-for="item in usuarios" :key="item.id" :value="item.id"> {{ (item.nomeCompleto)
                                    }}
                                </option>
                            </select>
                        </div>
                        <div class="pcm-ai-field" :class="aiFieldClass('setorDemanda_id')">
                            <label> Setor demandante
                                <i v-if="podeSolicitarEdicao" class="bi-pencil-square pcm-icone-pedido"
                                    title="Solicitar Edição" @click="abrirModal('setorDemanda_id')"></i>
                            </label>
                            <select style="text-align: left" v-model="PCM.setorDemanda_id"
                                :disabled="bloquearEdicaoCampos"
                                @change="atualizarPCM('setorDemanda_id', PCM.setorDemanda_id)"
                                :required="!PCM.setorDemanda_id">
                                <option v-for="item in setores" :key="item.id" :value="item.id"
                                    :style="{ 'font-weight': (item.nivel_hierarquico <= 2) ? 'bold' : '0' }">
                                    <span v-if="item.nivel_hierarquico == 3" style="color: red;"> &nbsp;&nbsp; </span>
                                    {{
                                        item.nivel_hierarquico == 3 ? "-" : '' }} {{ item.nome }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="submit direita pcm-doc-acoes" v-if="PCM.id == 'criar'">
                        <small class="pcm-doc-acoes__dica">Seu preenchimento é guardado automaticamente neste
                            navegador até o cadastro ser concluído.</small>
                        <button @click="criarPCM" :disabled="camposVazios || criando">
                            {{ criando ? 'Criando...' : `Criar ${PCM.tipo || 'Projeto'}` }}
                        </button>
                    </div>
                </fieldset>
            </div>
        </article>

        <template v-if="PCM.id !== 'criar'">
            <br>
            <h2 class="alinha-centro pcm-doc-secao-principal">Pedidos de Mudança</h2>
            <div v-if="pedidos_edicao && pedidos_edicao.length === 0" class="bloco2 margem alinha-centro">
                <span class="chip bg-cinza" style="font-size: 16px;">Nenhum pedido de mudança solicitado</span>
            </div>
            <div v-else-if="pedidos_edicao">
                <div v-for="(p, index) in pedidos_edicao" :key="p.id ?? index" class="alinha-v"
                    style="margin-block: 1rem; gap: 1rem;">
                    <h3 class="alinha-centro">{{ `${pedidos_edicao.length - index}º` }}</h3>
                    <fieldset class="grid-4 bloco2 margem" style="flex-grow: 1;">
                        <div>
                            <label>Campo</label>
                            <input type="text" readonly :value="labelCampoPedido(p)">
                        </div>
                        <div>
                            <label>Valor Original</label>
                            <textarea v-if="usaTextareaPedido(p)" :value="formatarValorPedidoExibicao(p, 'atual')"
                                readonly></textarea>
                            <textarea v-else-if="p.campo === 'objetivos_estrategicos'"
                                :value="resumoPedidoObjetivos(p).valorAntigo" readonly></textarea>
                            <input v-else-if="metaCampoPedido(p)?.tipo === 'date'" type="date"
                                :value="dataParaInput(p.valor_atual)" readonly />
                            <input v-else type="text" readonly :value="formatarValorPedidoExibicao(p, 'atual')" />
                        </div>
                        <div>
                            <label>Novo Valor</label>
                            <textarea v-if="usaTextareaPedido(p)" :value="formatarValorPedidoExibicao(p, 'novo')"
                                readonly></textarea>
                            <textarea v-else-if="p.campo === 'objetivos_estrategicos'"
                                :value="resumoPedidoObjetivos(p).novoValor" readonly></textarea>
                            <input v-else-if="metaCampoPedido(p)?.tipo === 'date'" type="date"
                                :value="dataParaInput(p.novo_valor)" readonly />
                            <input v-else type="text" readonly :value="formatarValorPedidoExibicao(p, 'novo')" />
                        </div>
                        <div>
                            <label>Solicitado Por</label>
                            <input type="text" readonly :value="p.solicitante?.name">
                        </div>
                        <div>
                            <label>Justificativa</label>
                            <textarea :value="p.justificativa" readonly></textarea>
                        </div>
                        <div v-if="p.obs">
                            <label>Observações</label>
                            <textarea :value="p.obs" readonly></textarea>
                        </div>
                        <div v-if="!somenteLeitura && p.status === 'pendente' && podeAprovarPedidos">
                            <label>Parecer do aprovador (opcional)</label>
                            <textarea v-model="obsAprovacaoPorPedido[p.id]"
                                placeholder="Registre observações ao aprovar ou reprovar"></textarea>
                        </div>
                        <div class="pedido-mudanca-status">
                            <div v-if="!somenteLeitura && p.status === 'pendente' && podeAprovarPedidos"
                                class="submit direita">
                                <button class="acao-secundaria reprovar"
                                    @click="aprovarPedidoEdicao(p.id, 'reprovado')">Reprovar</button>
                                <button class="acao-secundaria aprovar"
                                    @click="aprovarPedidoEdicao(p.id, 'aprovado')">Aprovar</button>
                            </div>
                            <div v-else-if="p.status === 'pendente'" class="direita">
                                <span class="chip bg-cinza" style="font-size: 16px;">Pendente</span>
                            </div>
                            <div v-else class="direita">
                                <span class="chip" style="font-size: 16px;"
                                    :class="p.status === 'aprovado' ? 'bg-sucesso' : 'bg-erro'">
                                    {{ p.status === 'aprovado' ? 'Aprovado' : 'Reprovado' }}
                                    <template v-if="p.aprovador?.name"> por {{ p.aprovador.name }}</template>
                                </span>
                            </div>
                        </div>
                    </fieldset>
                </div>
            </div>
            <div v-else class="loading">
                <div></div>
            </div>
        </template>
    </div>
    <div class="alinha-centro txt-erro fonte-maior" v-else>
        Cadastro não encontrado
    </div>

    <ModalSolicitarEdicao :mostrar="!!pedidoEdicao" :pedido-edicao="pedidoEdicao" :campos="campos" :config="config"
        :usuarios="usuarios || []" :setores="setores || []" :mensagem-edicao="mensagemEdicao"
        @fechar="pedidoEdicao = null, mensagemEdicao = null" @solicitar="criarPedidoEdicao" />
    <ModalCrudObjetivosEstrategicos :mostrar="mostrarCrudObjetivos"
        @fechar="mostrarCrudObjetivos = false" @alterado="onCatalogoObjetivosAlterado" />
</template>
<script>
import { useApiError } from "@/composables/useApiError";
import { useToast } from "vue-toastification";
import { sso } from "roboflex-thalamus-sso-lib";
import { getPermissao } from "@/services/permissao-service";
import { Money3Component } from "v-money3";
import { consultarSetores } from "@/services/usuario-setor";
import servicePCM from "@/services/servicePCM";
import serviceProjetos from "@/services/serviceProjetos";
import { cadastrarPrograma } from "@/services/serviceProgramas";
import ModalSolicitarEdicao from "@/components/Modais/PCM/ModalSolicitarEdicao.vue";
import ModalCrudObjetivosEstrategicos from "@/components/Modais/PCM/ModalCrudObjetivosEstrategicos.vue";
import logoEmpresa from "@/assets/img/thalamus.svg";
import { formatarDataBr, dataParaInput as formatarDataParaInput } from "@/utils/date";
import { createLocalId, ensureResultLocalIds } from "@/utils/ai-assistant.state";
import {
    aplicarRascunhoNoPcm,
    carregarRascunhoPcm,
    chaveRascunhoPcm,
    limparRascunhoPcm,
    salvarRascunhoPcm,
} from "@/utils/pcm-draft.storage";

const RASCUNHO_DEBOUNCE_MS = 800

export default {
    name: "pcmComponent",
    emits: ['getProjeto', 'getPAP', 'ai-field-edited', 'rascunho-restaurado'],
    components: {
        ModalSolicitarEdicao,
        ModalCrudObjetivosEstrategicos,
        money3: Money3Component,
    },
    setup() {
        const { handleError } = useApiError();
        const toast = useToast();
        return { handleApiError: handleError, toast };
    },


    props: {
        propPCM: {
            required: true
        },
        isGerente: {
            required: true
        },
        somenteLeitura: {
            type: Boolean,
            default: false,
        },
        aiFormState: {
            type: Object,
            default: null,
        },
    },
    computed: {
        bloquearEdicaoCampos() {
            if (this.somenteLeitura) return true
            if (this.PCM.id === 'criar') return false
            if (this.PCM.status === 'Aprovado') return true
            return !this.isAuditor116
        },
        /** Gerente, assessor ou auditor podem solicitar edição formal após aprovação do PCM. */
        podeSolicitarEdicao() {
            if (this.PCM.id === 'criar' || this.somenteLeitura) return false
            if (this.PCM.status === 'Aprovado') {
                return this.isGerente || this.isAuditor116
            }
            return this.isGerente
        },
        readonly() {
            return this.bloquearEdicaoCampos
        },
        bloquearEdicaoResultados() {
            return this.bloquearEdicaoCampos && this.PCM.tipo !== 'Programa'
        },
        parteDoComite() {
            return this.PCM?.comite_aprovacao?.map(i => i.id).includes(this.idUsuario) || false
        },
        podeAprovarPedidos() {
            const funcs = this.dadosUsuario || []
            if (this.PCM.nivel === 'Estratégico') {
                return funcs.includes(111) || funcs.includes(116) || this.parteDoComite
            }
            return funcs.includes(56)
        },
        /** Edição local dos resultados — somente na criação do PCM (aba Proposta). */
        podeEditarResultadosLocalmente() {
            if (this.somenteLeitura) return false
            if (this.PCM.tipo === 'Programa') return false
            return this.PCM.id === 'criar'
        },
        /** Persistência direta na API — somente na criação do PCM. */
        podeEditarResultados() {
            return this.PCM.id === 'criar' && !this.somenteLeitura && this.PCM.tipo !== 'Programa'
        },
        isAuditor116() {
            return Array.isArray(this.dadosUsuario) && this.dadosUsuario.includes(116)
        },
        /** Estimativa da mudança é a soma dos resultados: visível, sem pedido de alteração. */
        podeVerCustoEstimado() {
            return this.PCM.id == 'criar' || this.parteDoComite || this.isGerente || this.isAuditor116
        },
        /** Soma dos custos informados em «Resultados a serem alcançados» (igual Reunião de Aprovação). */
        custoTotalEstado() {
            const resultados = Array.isArray(this.PCM.resultados) ? this.PCM.resultados : []
            if (resultados.some((res) => res?.cost_state === 'pending')) return 'pending'
            if (resultados.some((res) => res?.cost_state === 'estimated')) return 'estimated'
            if (resultados.length && resultados.every((res) => res?.sem_custo || res?.cost_state === 'no_cost')) {
                return 'no_cost'
            }
            return 'known'
        },
        custoTotalResultados: {
            get() {
                return (this.PCM.resultados || []).reduce((acc, res) => {
                    if (res?.sem_custo) return acc
                    return acc + this.converterCustoParaNumero(res?.custo)
                }, 0)
            },
            set() {
                /* valor derivado; money3 em modo somente leitura ainda emite atualizações */
            },
        },
        podeSolicitarPedidoResultado() {
            return this.podeSolicitarEdicao && this.bloquearEdicaoResultados
        },
        camposVazios() {
            const campos = [
                'nome',
                'nivel',
                'tipo',
                'finalidade',
                'area',
                'setor_id',
                'dtInicio',
                'gerentePCM_id',
                'solicitante_id',
                'setorDemanda_id'
            ];
            if (this.PCM.tipo !== 'Programa') {
                campos.push(
                    'dtEstimadaFinal',
                    'objetivo_geral',
                    'descricao_problema',
                    'ganho_operacional_esperado',
                    'escopo'
                );
            }
            if (campos.some(item => !this.PCM[item] || this.PCM[item] === '')) {
                return true;
            }
            if (this.PCM.id === 'criar' && this.PCM.tipo !== 'Programa') {
                const res = this.PCM.resultados || [];
                const temResultadoCompleto = res.some(
                    (r) => ['nome', 'descricao', 'evidencias']
                        .every((campo) => String(r?.[campo] || '').trim() !== '')
                );
                if (!temResultadoCompleto) return true;
            }
            return false;
        },
        temAlteracoesObjetivosPendentes() {
            if (!this.bloquearEdicaoCampos || !this.podeSolicitarEdicao) return false
            return this.serializarObjetivosParaComparacao() !== this.objetivosSnapshotBase
        },
    },
    data() {
        return {
            logoEmpresa,
            isDragging: false,
            dragDepth: 0,
            uploading: false,
            baixandoPdf: false,
            stagedFiles: [],

            /** Envio do cadastro em andamento (evita duplo clique / duplo POST). */
            criando: false,
            /** Chave do rascunho local (por usuário e tipo). Definida apenas no modo «criar». */
            chaveRascunho: null,
            /** Envelope do rascunho encontrado ao abrir a tela, aguardando decisão do usuário. */
            rascunhoPendente: null,

            storageUrl: process.env.VUE_APP_ROOT_STORAGE,

            campos: servicePCM.campos,
            pedidoEdicao: null,
            mensagemEdicao: null,
            mostrarCrudObjetivos: false,
            pedidos_edicao: null,
            obsAprovacaoPorPedido: {},
            resultadosSnapshotBasePedido: null,

            PCM: this.propPCM,

            dadosUsuario: null,
            idUsuario: null,

            setores: [],
            usuarios: null,

            novoSetorEnvolvido: null,

            /** IDs marcados para exclusão no backend ao clicar em «Salvar alterações» */
            resultadosIdsExcluir: [],
            salvandoResultados: false,
            /** Estado salvo dos resultados (JSON) para comparar mudanças locais */
            resultadosSnapshotBase: '',
            objetivosSnapshotBase: '',
            objetivosSnapshotBasePedido: null,

            impacto_viabilidade: [],

            objetivos: [],

            config: {
                masked: false,
                prefix: "R$ ",
                thousands: ".",
                decimal: ",",
                precision: 2,
            },
        };
    },

    mounted() {
        const prevent = (e) => { e.preventDefault(); };
        window.addEventListener('dragover', prevent);
        window.addEventListener('drop', prevent);
        this._preventDnD = prevent;
        /* Fechar/recarregar a aba antes do debounce: grava o rascunho imediatamente. */
        this._flushRascunho = () => this.salvarRascunhoAgora('beforeunload');
        window.addEventListener('beforeunload', this._flushRascunho);
    },
    unmounted() {
        window.removeEventListener('dragover', this._preventDnD);
        window.removeEventListener('drop', this._preventDnD);
        window.removeEventListener('beforeunload', this._flushRascunho);
        clearTimeout(this._rascunhoTimer);
        /* Último flush: garante que a saída da tela (troca de rota) não perca o que foi digitado. */
        this.salvarRascunhoAgora('saida');
    },

    async created() {
        /* Resultados e snapshot antes do primeiro await — evita flash do botão «Salvar» na montagem */
        if (this.PCM.id === 'criar' && this.PCM.tipo !== 'Programa') {
            this.ensureResultadosPcmCriacao()
        } else if (this.PCM.id !== 'criar' && this.PCM.tipo !== 'Programa') {
            this.ensureResultadosParaEdicao()
            this.syncResultadosSnapshotBase()
            this.resultadosSnapshotBasePedido = JSON.stringify(
                (this.PCM.resultados || []).map((r) => this.normalizarResultadoParaPayload(r))
            )
        }
        if (this.PCM.id !== 'criar') {
            this.syncObjetivosSnapshotBase()
            this.objetivosSnapshotBasePedido = JSON.stringify(this.normalizarObjetivosParaPayload())
        }
        this.dadosUsuario = await getPermissao();
        const usuarioLogado = sso.getUsuarioLogado()
        this.idUsuario = usuarioLogado.id
        !this.PCM.solicitante_id ? this.PCM.solicitante_id = usuarioLogado.id : null
        !this.PCM.setores_envolvidos ? this.PCM.setores_envolvidos = [] : null
        if (this.PCM.id === 'criar' && !this.somenteLeitura) {
            this.chaveRascunho = chaveRascunhoPcm(this.PCM.tipo, this.idUsuario)
            this.rascunhoPendente = carregarRascunhoPcm(this.chaveRascunho)
        }
        await this.getSetores()
        if (this.PCM.id === 'criar') {
            await this.carregarCatalogosTenant()
        }
        if (this.PCM.id !== 'criar') {
            await this.getPedidosEdicao()
        }
    },

    watch: {
        /**
         * Sincroniza só quando o pai substitui o objeto PCM (ex.: após getPCM).
         * Evita deep watch: mutar resultados disparava o handler, restaurava dados do pai
         * e zerava resultadosIdsExcluir — exclusões pareciam «voltar» após salvar.
         */
        propPCM(newVal) {
            if (newVal === this.PCM) return
            this.PCM = newVal
            this.resultadosIdsExcluir = []
            if (this.PCM?.id !== 'criar' && this.PCM?.tipo !== 'Programa') {
                this.ensureResultadosParaEdicao()
            }
            this.syncResultadosSnapshotBase()
            this.resultadosSnapshotBasePedido = JSON.stringify(
                (this.PCM.resultados || []).map((r) => this.normalizarResultadoParaPayload(r))
            )
            this.syncObjetivosSnapshotBase()
            this.objetivosSnapshotBasePedido = JSON.stringify(this.normalizarObjetivosParaPayload())
            if (this.PCM?.id !== 'criar') {
                this.getPedidosEdicao()
            }
        },
        /**
         * Autosave do rascunho (modo «criar»). Sem efeitos colaterais sobre os dados:
         * apenas agenda a gravação em localStorage com debounce.
         */
        PCM: {
            deep: true,
            handler() {
                this.agendarSalvarRascunho()
            },
        },
        objetivos: {
            deep: true,
            handler() {
                this.agendarSalvarRascunho()
            },
        },
        impacto_viabilidade: {
            deep: true,
            handler() {
                this.agendarSalvarRascunho()
            },
        },
    },

    methods: {
        formatarDataBr,
        aiFieldClass(fieldPath) {
            const ownership = this.aiFormState?.ownership || {}
            const direct = ownership[fieldPath]
            const nested = Object.entries(ownership)
                .filter(([path]) => path.startsWith(`${fieldPath}.`))
                .map(([, owner]) => owner)
            const states = direct ? [direct, ...nested] : nested
            if (states.includes('contested')) return 'pcm-ai-field--contested'
            if (states.includes('ai_suggested')) return 'pcm-ai-field--suggested'
            if (states.includes('confirmed')) return 'pcm-ai-field--confirmed'
            return ''
        },
        marcarCampoManual(fieldPath) {
            this.$emit('ai-field-edited', fieldPath)
        },
        marcarResultadoManual(resultado, campo) {
            if (!resultado.local_id) resultado.local_id = createLocalId()
            const suffix = campo ? `.${campo}` : ''
            this.$emit('ai-field-edited', `resultados.${resultado.local_id}${suffix}`)
        },
        async baixarPdf() {
            if (!this.PCM?.id || this.PCM.id === 'criar' || this.baixandoPdf) return

            this.baixandoPdf = true
            try {
                await servicePCM.baixarPdf(this.PCM.id, this.PCM.codigo)
            } catch (error) {
                this.handleApiError(error)
            } finally {
                this.baixandoPdf = false
            }
        },
        onChangeDtConclusaoResultadoAuditor() {
            if (this.PCM.id === 'criar' || !this.podeEditarResultados) return
            this.persistirResultados(this.resultadosIdsExcluir)
        },
        onChangeDtPrevistaResultado(resultado) {
            this.marcarResultadoManual(resultado, 'dt_prevista_conclusao')
            if (this.PCM.id === 'criar') return
        },
        serializarResultadosParaComparacao(resultados = []) {
            const normalizados = (resultados || []).map((r) => ({
                id: r.id ?? null,
                local_id: r.local_id ?? null,
                nome: (r.nome ?? '').toString().trim(),
                problema: (r.problema ?? '').toString().trim(),
                descricao: (r.descricao ?? '').toString().trim(),
                evidencias: (r.evidencias ?? '').toString().trim(),
                dt_prevista_conclusao: r.dt_prevista_conclusao === '' || r.dt_prevista_conclusao == null
                    ? null
                    : String(r.dt_prevista_conclusao).slice(0, 10),
                dt_conclusao: r.dt_conclusao === '' || r.dt_conclusao == null
                    ? null
                    : String(r.dt_conclusao).slice(0, 10),
                custo: r.custo === '' || r.custo == null
                    ? null
                    : Number(this.converterCustoParaNumero(r.custo)),
                status: this.statusResultadoNormalizado(r),
            }))
            return JSON.stringify(normalizados)
        },
        syncResultadosSnapshotBase() {
            this.resultadosSnapshotBase = this.serializarResultadosParaComparacao(this.PCM.resultados || [])
        },
        ensureResultadosParaEdicao() {
            if (!this.PCM.resultados || !Array.isArray(this.PCM.resultados)) {
                this.PCM.resultados = []
            }
            if (this.PCM.resultados.length === 0) {
                this.PCM.resultados.push({
                    local_id: createLocalId(),
                    nome: '',
                    problema: '',
                    descricao: '',
                    evidencias: '',
                    dt_prevista_conclusao: null,
                    dt_conclusao: null,
                    custo: null,
                    sem_custo: false,
                    status: 'pendente',
                })
            }
            ensureResultLocalIds(this.PCM.resultados)
            this.PCM.resultados.forEach((r) => {
                if (r.nome === undefined) r.nome = ''
                if (r.problema === undefined) r.problema = ''
                if (!r.status) r.status = 'pendente'
                if (r.sem_custo == null) r.sem_custo = false
                if (r.sem_custo) r.custo = null
                if (!['known', 'estimated', 'no_cost', 'pending'].includes(r.cost_state)) {
                    r.cost_state = r.sem_custo ? 'no_cost' : (r.custo == null || r.custo === '' ? 'pending' : 'known')
                }
                if (r.dt_prevista_conclusao === undefined) r.dt_prevista_conclusao = null
                if (r.dt_conclusao === undefined) r.dt_conclusao = null
                if (r.dt_prevista_conclusao) r.dt_prevista_conclusao = String(r.dt_prevista_conclusao).slice(0, 10)
                if (r.dt_conclusao) r.dt_conclusao = String(r.dt_conclusao).slice(0, 10)
            })
        },
        ensureResultadosPcmCriacao() {
            if (!this.PCM.resultados || !Array.isArray(this.PCM.resultados)) {
                this.PCM.resultados = []
            }
            if (this.PCM.resultados.length === 0) {
                this.PCM.resultados.push({
                    local_id: createLocalId(),
                    nome: '',
                    problema: '',
                    descricao: this.PCM.proposito_mudanca || this.PCM.escopo || '',
                    evidencias: this.PCM.possivel_solucao || '',
                    dt_prevista_conclusao: null,
                    dt_conclusao: null,
                    custo: null,
                    sem_custo: false,
                    status: 'pendente',
                })
            }
            ensureResultLocalIds(this.PCM.resultados)
            this.PCM.resultados.forEach((r) => {
                if (r.nome === undefined) r.nome = ''
                if (r.problema === undefined) r.problema = ''
                if (!r.status) r.status = 'pendente'
                if (r.sem_custo == null) r.sem_custo = false
                if (r.sem_custo) r.custo = null
                if (!['known', 'estimated', 'no_cost', 'pending'].includes(r.cost_state)) {
                    r.cost_state = r.sem_custo ? 'no_cost' : (r.custo == null || r.custo === '' ? 'pending' : 'known')
                }
                if (r.dt_prevista_conclusao === undefined) r.dt_prevista_conclusao = null
                if (r.dt_conclusao === undefined) r.dt_conclusao = null
                if (r.dt_prevista_conclusao) r.dt_prevista_conclusao = String(r.dt_prevista_conclusao).slice(0, 10)
                if (r.dt_conclusao) r.dt_conclusao = String(r.dt_conclusao).slice(0, 10)
            })
        },
        adicionarResultado() {
            if (this.PCM.id === 'criar') {
                this.ensureResultadosPcmCriacao()
            } else {
                this.ensureResultadosParaEdicao()
            }
            const resultado = {
                local_id: createLocalId(),
                nome: '',
                problema: '',
                descricao: '',
                evidencias: '',
                dt_prevista_conclusao: null,
                dt_conclusao: null,
                custo: null,
                sem_custo: false,
                status: 'pendente',
            }
            this.PCM.resultados.push(resultado)
            this.marcarResultadoManual(resultado)
        },
        handleAdicionarResultado() {
            if (this.podeSolicitarPedidoResultado) {
                this.abrirPedidoNovoResultado()
                return
            }
            this.adicionarResultado()
        },
        onToggleSemCustoResultado(res, checked) {
            if (!this.podeEditarResultadosLocalmente) return
            this.marcarResultadoManual(res, 'sem_custo')
            res.sem_custo = !!checked
            if (res.sem_custo) {
                res.custo = null
                res.cost_state = 'no_cost'
            } else if (res.custo == null || res.custo === '') {
                res.cost_state = 'pending'
            }
        },
        onCustoPendenteInput(res) {
            if (res.custo === '' || res.custo == null || !Number.isFinite(Number(res.custo))) return
            res.sem_custo = false
            res.cost_state = 'known'
            this.marcarResultadoManual(res, 'custo')
        },
        removerResultado(index) {
            const removido = this.PCM.resultados?.[index]
            if (removido) this.marcarResultadoManual(removido)
            if (this.PCM.id === 'criar') {
                this.ensureResultadosPcmCriacao()
                this.PCM.resultados.splice(index, 1)
                if (this.PCM.resultados.length === 0) {
                    this.PCM.resultados.push({
                        local_id: createLocalId(),
                        nome: '',
                        problema: '',
                        descricao: '',
                        evidencias: '',
                        dt_prevista_conclusao: null,
                        dt_conclusao: null,
                        custo: null,
                        sem_custo: false,
                        status: 'pendente',
                    })
                }
                return
            }
            if (this.bloquearEdicaoResultados) {
                const row = this.PCM.resultados[index]
                if (row?.id != null) {
                    this.abrirPedidoExcluirResultado(row, index)
                }
                return
            }
            const row = this.PCM.resultados[index]
            if (row?.id) {
                this.resultadosIdsExcluir.push(row.id)
            }
            this.PCM.resultados.splice(index, 1)
            if (this.PCM.resultados.length === 0) {
                this.PCM.resultados.push({
                    local_id: createLocalId(),
                    nome: '',
                    problema: '',
                    descricao: '',
                    evidencias: '',
                    dt_prevista_conclusao: null,
                    dt_conclusao: null,
                    custo: null,
                    sem_custo: false,
                    status: 'pendente',
                })
            }
            this.ensureResultadosParaEdicao()
        },
        async excluirAnexo(id) {
            await servicePCM.excluirArquivo(this.PCM.id, id)
            this.PCM.anexos = this.PCM.anexos.filter(i => i.id != id)
        },
        onDragEnter(e) {
            e.preventDefault();
            this.dragDepth++;
            this.isDragging = true;
        },
        onDragOver(e) {
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        },
        onDragLeave(e) {
            e.preventDefault();
            this.dragDepth = Math.max(0, this.dragDepth - 1);
            if (this.dragDepth === 0) this.isDragging = false;
        },
        onDrop(e) {
            e.preventDefault();
            this.dragDepth = 0;
            this.isDragging = false;
            const files = Array.from(e.dataTransfer?.files || []);
            if (files.length) this.processFiles(files); // sua lógica de upload/staging
        },

        abrirFileDialog() {
            this.$refs.fileInput?.click();
        },

        handleFileSelect(e) {
            const files = Array.from(e.target.files || []);
            if (!files.length) return;
            this.processFiles(files);
            e.target.value = '';
        },

        handleDrop(e) {
            this.isDragging = false;
            const files = Array.from(e.dataTransfer?.files || []);
            if (!files.length) return;
            this.processFiles(files);
        },

        processFiles(files) {
            if (this.PCM.id === 'criar') {
                for (const f of files) {
                    this.stagedFiles.push({ file: f, categoria: this.categoriaAtual });
                }
                return;
            }

            this.uploadArquivos(files, this.PCM.id);
        },

        removerStaged(i) {
            this.stagedFiles.splice(i, 1);
        },

        /** Retorna `true` quando o envio foi concluído; erros são exibidos via toast. */
        async uploadArquivos(files, pcmId) {
            try {
                this.uploading = true;

                const formData = new FormData();
                for (const f of files) formData.append('arquivo[]', f);

                await servicePCM.adicionarArquivo(formData, pcmId);

                const atualizado = await servicePCM.getPCM(pcmId);
                this.PCM.anexos = atualizado?.anexos ?? [];
                return true;
            } catch (err) {
                this.handleApiError(err);
                return false;
            } finally {
                this.uploading = false;
            }
        },

        converterCustoParaNumero(valor) {
            if (valor === '' || valor == null) return 0
            if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0

            const bruto = String(valor).replace(/[^\d,.-]/g, '')
            if (!bruto) return 0

            const temVirgula = bruto.includes(',')
            const temPonto = bruto.includes('.')
            let normalizado = bruto

            if (temVirgula && temPonto) {
                normalizado = bruto.replace(/\./g, '').replace(',', '.')
            } else if (temVirgula) {
                normalizado = bruto.replace(',', '.')
            } else {
                normalizado = bruto
            }

            const numero = Number(normalizado)
            return Number.isFinite(numero) ? numero : 0
        },
        statusResultadoNormalizado(res) {
            const s = (res?.status || 'pendente').toString().toLowerCase()
            if (s === 'validado' || s === 'concluido') return s
            return 'pendente'
        },
        async persistirResultados(resultadosExcluirIds = []) {
            if (this.PCM.id === 'criar' || !this.podeEditarResultados) return false
            const resultados = (this.PCM.resultados || []).map((r, i) => ({
                id: r.id,
                local_id: r.local_id,
                descricao: r.descricao,
                evidencias: r.evidencias,
                dt_prevista_conclusao: r.dt_prevista_conclusao === '' || r.dt_prevista_conclusao == null
                    ? null
                    : String(r.dt_prevista_conclusao).slice(0, 10),
                dt_conclusao: r.dt_conclusao === '' || r.dt_conclusao == null
                    ? null
                    : String(r.dt_conclusao).slice(0, 10),
                sem_custo: !!r.sem_custo,
                custo: r.sem_custo || r.custo === '' || r.custo == null ? null : r.custo,
                status: this.statusResultadoNormalizado(r),
                indice: i,
            }))
            try {
                await servicePCM.salvarResultadosPcm(this.PCM.id, resultados, resultadosExcluirIds)
                this.PCM.estimativa_custo = this.custoTotalResultados
                this.$emit('getProjeto')
                this.$emit('getPAP')
                return true
            } catch (e) {
                this.handleApiError(e)
                return false
            }
        },
        async criarPedidoEdicao(pedido) {
            if (!this.podeSolicitarEdicao) return
            const dados = pedido || this.pedidoEdicao
            if (dados?.campo === 'estimativa_custo') return
            this.mensagemEdicao = await servicePCM.criarPedidoEdicao(dados)
            this.syncResultadosSnapshotBase()
            this.resultadosSnapshotBasePedido = JSON.stringify(
                (this.PCM.resultados || []).map((r) => this.normalizarResultadoParaPayload(r))
            )
            this.objetivosSnapshotBasePedido = JSON.stringify(this.normalizarObjetivosParaPayload())
            this.syncObjetivosSnapshotBase()
            this.resultadosIdsExcluir = []
            this.$emit('getProjeto')
            await this.getPedidosEdicao()
        },
        abrirModal(campo) {
            if (!this.podeSolicitarEdicao || campo === 'estimativa_custo') return
            this.pedidoEdicao = {
                pcm_id: this.PCM.id,
                campo,
            }
        },
        normalizarResultadoParaPayload(resultado) {
            return {
                id: resultado?.id ?? null,
                local_id: resultado?.local_id ?? null,
                nome: resultado?.nome ?? '',
                problema: resultado?.problema ?? '',
                descricao: resultado?.descricao ?? '',
                evidencias: resultado?.evidencias ?? '',
                dt_prevista_conclusao: resultado?.dt_prevista_conclusao === '' || resultado?.dt_prevista_conclusao == null
                    ? null
                    : String(resultado.dt_prevista_conclusao).slice(0, 10),
                dt_conclusao: resultado?.dt_conclusao === '' || resultado?.dt_conclusao == null
                    ? null
                    : String(resultado.dt_conclusao).slice(0, 10),
                sem_custo: !!resultado?.sem_custo,
                custo: resultado?.sem_custo || resultado?.custo === '' || resultado?.custo == null
                    ? null
                    : resultado.custo,
                status: this.statusResultadoNormalizado(resultado),
            }
        },
        valorAtualAtributoResultado(resultado, atributo) {
            if (atributo === 'sem_custo') return resultado?.sem_custo ? '1' : '0'
            if (atributo === 'custo') {
                if (resultado?.sem_custo || resultado?.custo === '' || resultado?.custo == null) return ''
                return String(this.converterCustoParaNumero(resultado.custo))
            }
            if (atributo === 'dt_prevista_conclusao' || atributo === 'dt_conclusao') {
                if (!resultado?.[atributo]) return ''
                return String(resultado[atributo]).slice(0, 10)
            }
            return resultado?.[atributo] ?? ''
        },
        podeSolicitarPedidoCampoResultado(res) {
            return this.podeSolicitarPedidoResultado && res?.id != null
        },
        abrirPedidoCampoResultado(res, idx, atributo) {
            if (!this.podeSolicitarPedidoCampoResultado(res)) return
            this.pedidoEdicao = {
                pcm_id: this.PCM.id,
                campo: servicePCM.montarCampoPedidoResultado(res.id, atributo),
                valor_atual: this.valorAtualAtributoResultado(res, atributo),
                valor_atual_sem_custo: res.sem_custo ? '1' : '0',
                novo_valor: atributo === 'sem_custo' ? '1' : null,
                _label: servicePCM.labelCampoPedido(
                    servicePCM.montarCampoPedidoResultado(res.id, atributo),
                    { indice: idx + 1 },
                ),
            }
        },
        abrirPedidoNovoResultado() {
            if (!this.podeSolicitarPedidoResultado) return
            this.pedidoEdicao = {
                pcm_id: this.PCM.id,
                campo: servicePCM.montarCampoPedidoNovoResultado(),
                valor_atual: '',
                novo_valor: null,
                _label: 'Nova entrega',
            }
        },
        abrirPedidoExcluirResultado(res, idx) {
            if (!this.podeSolicitarPedidoCampoResultado(res)) return
            this.pedidoEdicao = {
                pcm_id: this.PCM.id,
                campo: servicePCM.montarCampoPedidoExcluirResultado(res.id),
                valor_atual: JSON.stringify(this.normalizarResultadoParaPayload(res)),
                novo_valor: '(exclusão)',
                _label: servicePCM.labelCampoPedido(
                    servicePCM.montarCampoPedidoExcluirResultado(res.id),
                    { indice: idx + 1 },
                ),
            }
        },
        labelCampoPedido(pedido) {
            const parsed = servicePCM.parseCampoPedidoResultado(pedido?.campo)
            let indice = null
            if (parsed?.resultadoId != null) {
                indice = servicePCM.indiceResultadoPorId(this.PCM.resultados, parsed.resultadoId)
            }
            return servicePCM.labelCampoPedido(pedido?.campo, { indice })
                || this.campos[pedido?.campo]?.label
                || pedido?.campo
        },
        metaCampoPedido(pedido) {
            return servicePCM.metaCampoPedido(pedido?.campo) || this.campos[pedido?.campo] || null
        },
        usaTextareaPedido(pedido) {
            const parsed = servicePCM.parseCampoPedidoResultado(pedido?.campo)
            if (!parsed) return false
            if (parsed.tipo === 'lista' || parsed.tipo === 'novo' || parsed.tipo === 'excluir') return true
            if (parsed.tipo === 'campo' && (parsed.atributo === 'descricao' || parsed.atributo === 'evidencias')) {
                return true
            }
            return false
        },
        formatarValorPedidoExibicao(pedido, qual) {
            const parsed = servicePCM.parseCampoPedidoResultado(pedido?.campo)
            if (parsed?.tipo === 'lista') {
                return qual === 'atual'
                    ? this.resumoPedidoResultados(pedido).valorAntigo
                    : this.resumoPedidoResultados(pedido).novoValor
            }
            if (parsed) {
                return servicePCM.formatarValorPedidoResultado(pedido, qual)
            }
            return this.labelSelectPedido(pedido.campo, qual === 'atual' ? pedido.valor_atual : pedido.novo_valor)
        },
        async getPedidosEdicao() {
            if (!this.PCM?.id || this.PCM.id === 'criar') return
            this.pedidos_edicao = await servicePCM.getPedidosEdicao(this.PCM.id)
        },
        async aprovarPedidoEdicao(id, status) {
            if (this.somenteLeitura) return
            const obs = this.obsAprovacaoPorPedido[id] ?? null
            await servicePCM.aprovarPedidoEdicao(id, status, obs)
            delete this.obsAprovacaoPorPedido[id]
            this.$emit('getProjeto')
            await this.getPedidosEdicao()
        },
        dataParaInput(valor) {
            return formatarDataParaInput(valor)
        },
        parseResultadosPedido(valor) {
            if (!valor) return []
            try {
                const parsed = JSON.parse(valor)
                return Array.isArray(parsed) ? parsed : []
            } catch (_) {
                return []
            }
        },
        resumoPedidoResultados(pedido) {
            const anterior = this.parseResultadosPedido(pedido?.valor_atual)
            const proposto = this.parseResultadosPedido(pedido?.novo_valor)
            const blocosAntigo = [`Total de itens antes: ${anterior.length}`]
            const blocosNovo = [`Total de itens na proposta: ${proposto.length}`]
            anterior.forEach((item, indice) => {
                blocosAntigo.push(`—\nItem ${indice + 1}:\n${(item?.descricao || '').trim() || '(vazio)'}`)
            })
            proposto.forEach((item, indice) => {
                blocosNovo.push(`—\nItem ${indice + 1}:\n${(item?.descricao || '').trim() || '(vazio)'}`)
            })
            return {
                valorAntigo: blocosAntigo.join('\n'),
                novoValor: blocosNovo.join('\n'),
            }
        },
        labelSelectPedido(campo, valor) {
            if (valor == null || valor === '') return '-'
            if (campo === 'gerentePCM_id' || campo === 'solicitante_id') {
                const u = (this.usuarios || []).find((item) => String(item.id) === String(valor))
                return u?.nomeCompleto ?? valor
            }
            if (campo === 'setor_id' || campo === 'setorDemanda_id') {
                const s = (this.setores || []).find((item) => String(item.id) === String(valor))
                return s?.nome ?? valor
            }
            const lista = this.campos[campo]?.lista
            if (Array.isArray(lista)) return valor
            return valor
        },
        /* ---------------------------------------------------------------------
         * Rascunho local (localStorage)
         * ------------------------------------------------------------------ */
        formatarDataHoraRascunho(iso) {
            const data = new Date(iso)
            if (Number.isNaN(data.getTime())) return '-'
            return data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
        },
        agendarSalvarRascunho() {
            if (this.PCM?.id !== 'criar' || !this.chaveRascunho) return
            clearTimeout(this._rascunhoTimer)
            this._rascunhoTimer = setTimeout(() => this.salvarRascunhoAgora('autosave'), RASCUNHO_DEBOUNCE_MS)
        },
        /**
         * Grava o estado atual do formulário. Enquanto houver um rascunho anterior aguardando
         * decisão (banner visível), o autosave fica pausado para não sobrescrevê-lo — exceto
         * no pré-envio, quando o usuário já se comprometeu com o conteúdo atual.
         */
        salvarRascunhoAgora(motivo = 'autosave') {
            if (this.PCM?.id !== 'criar' || !this.chaveRascunho || this.somenteLeitura) return false
            if (this.rascunhoPendente && motivo !== 'pre-envio') return false
            return salvarRascunhoPcm(
                this.chaveRascunho,
                this.PCM,
                {
                    objetivos_estrategicos: this.listaObjetivosEstrategicos(),
                    impacto_viabilidade: this.PCM.impacto_viabilidade ?? this.impacto_viabilidade,
                },
                { motivo, anexosPendentes: this.stagedFiles.length },
            )
        },
        limparRascunho() {
            clearTimeout(this._rascunhoTimer)
            limparRascunhoPcm(this.chaveRascunho)
            this.rascunhoPendente = null
        },
        restaurarRascunho() {
            const envelope = this.rascunhoPendente
            if (!envelope?.dados) return

            const restaurados = aplicarRascunhoNoPcm(this.PCM, envelope.dados, {
                objetivos: this.objetivos,
                impactos: this.impacto_viabilidade,
            })
            if (!Array.isArray(this.PCM.setores_envolvidos)) this.PCM.setores_envolvidos = []
            if (this.PCM.tipo !== 'Programa') this.ensureResultadosPcmCriacao()

            this.rascunhoPendente = null
            this.$emit('rascunho-restaurado', restaurados)
            this.toast.success(
                envelope.anexosPendentes
                    ? 'Rascunho restaurado. Os anexos precisam ser adicionados novamente.'
                    : 'Rascunho restaurado.',
            )
        },
        descartarRascunho() {
            this.limparRascunho()
        },

        /* ---------------------------------------------------------------------
         * Criação da proposta (+ projeto) — fluxo atômico
         * ------------------------------------------------------------------ */
        resolverDemandanteProjeto({ avisar = true } = {}) {
            const setorDemandante = (this.setores || []).find((setor) => setor.id == this.PCM.setorDemanda_id)
            const demandante = setorDemandante?.responsavel_id
                ?? this.PCM.solicitante_id
                ?? this.PCM.gerentePCM_id
                ?? this.idUsuario

            if (avisar && !setorDemandante?.responsavel_id) {
                this.toast.warning(
                    'Setor demandante sem responsável cadastrado. Usando o solicitante como demandante do projeto.',
                )
            }
            return demandante ?? null
        },
        /**
         * Monta o payload de criação **sem mutar `this.PCM`**: se a API falhar, o formulário
         * permanece exatamente como o usuário deixou.
         */
        montarPayloadCriacao() {
            const payload = { ...this.PCM }
            delete payload.id
            delete payload.codigo
            delete payload.pcm_codigo
            delete payload.anexos
            delete payload.objetivos_estrategicos

            payload.status = this.PCM.nivel !== 'Estratégico' ? 'Aprovado' : 'Aguardando Aprovação'

            payload.impacto_viabilidade = (this.PCM.impacto_viabilidade ?? this.impacto_viabilidade ?? [])
                .filter((item) => item?.impactoViabilidade_id != null)
                .map((item) => ({
                    impactoViabilidade_id: item.impactoViabilidade_id,
                    concordo: item.concordo === true,
                    justificativa: item.justificativa ?? '',
                }))

            payload.setores_envolvidos = (this.PCM.setores_envolvidos || [])
                .map((i) => ({ setor_id: i.setor_id ?? i.id, justificativa: i.justificativa ?? '' }))
                .filter((i) => i.setor_id != null)

            payload.objetivos = this.payloadObjetivos().map((o) => ({
                objetivo_id: o.objetivo_id,
                justificativa: o.justificativa ?? null,
            }))

            if (this.PCM.tipo !== 'Programa') {
                this.ensureResultadosPcmCriacao()
                if (this.PCM.nivel !== 'Operacional') {
                    payload.estimativa_custo = this.custoTotalResultados
                }
                payload.resultados = (this.PCM.resultados || []).map((r, i) => ({
                    local_id: r.local_id,
                    nome: r.nome ?? '',
                    problema: r.problema ?? '',
                    descricao: r.descricao ?? '',
                    evidencias: r.evidencias ?? '',
                    dt_prevista_conclusao: r.dt_prevista_conclusao === '' || r.dt_prevista_conclusao == null
                        ? null
                        : String(r.dt_prevista_conclusao).slice(0, 10),
                    sem_custo: !!r.sem_custo,
                    custo: r.sem_custo || r.custo === '' || r.custo == null
                        ? null
                        : this.converterCustoParaNumero(r.custo),
                    status: 'pendente',
                    indice: i,
                }))
            } else {
                delete payload.resultados
            }

            if (this.PCM.tipo === 'Projeto') {
                payload.projeto = {
                    demandante_id: this.resolverDemandanteProjeto(),
                    status: this.PCM.nivel === 'Estratégico' ? 'Proposto' : 'Em andamento',
                }
            }

            return payload
        },
        async criarPCM() {
            if (this.criando || this.camposVazios) return
            this.criando = true

            /* Snapshot antes do envio: se algo der errado, o usuário não perde o formulário. */
            this.salvarRascunhoAgora('pre-envio')

            let response
            try {
                const payload = this.montarPayloadCriacao()
                if (payload.tipo === 'Projeto' && !payload.projeto?.demandante_id) {
                    throw new Error('Não foi possível definir o demandante do projeto.')
                }
                response = await servicePCM.criarPCM(payload)
            } catch (error) {
                this.handleApiError(error)
                this.toast.info(
                    'Nada foi criado. Seus dados continuam no formulário e um rascunho foi guardado neste navegador.',
                )
                this.criando = false
                return
            }

            /* A partir daqui a proposta (e o projeto, quando aplicável) existe no servidor. */
            this.limparRascunho()
            this.PCM.id = response.id
            this.PCM.codigo = response.codigo
            this.PCM.pcm_codigo = response.codigo

            await this.enviarAnexosPendentes(response.id)

            try {
                await this.concluirCriacaoElemento(response)
            } catch (error) {
                this.handleApiError(error)
            } finally {
                this.criando = false
            }
        },
        async enviarAnexosPendentes(pcmId) {
            if (!this.stagedFiles.length) return
            const files = this.stagedFiles.map((it) => it.file)
            const ok = await this.uploadArquivos(files, pcmId)
            if (ok) {
                this.stagedFiles = []
                return
            }
            this.toast.warning(
                'A proposta foi criada, mas os anexos não puderam ser enviados. Adicione-os novamente na aba Proposta de Projeto.',
            )
        },
        /**
         * Após a criação: navega para o projeto (criado atomicamente pelo backend) ou
         * cria o programa. Mantém compatibilidade com backend antigo que ainda não devolve
         * `projeto_id` (fallback em duas etapas).
         */
        async concluirCriacaoElemento(response) {
            if (this.PCM.tipo === 'Projeto') {
                let projetoId = response.projeto_id ?? null
                if (!projetoId) {
                    projetoId = await this.criarProjetoLegado(response.id)
                }
                if (!projetoId) return
                this.$emit('getProjeto', projetoId)
                this.$router.push({ name: 'projetoView', params: { idProjeto: projetoId } })
                return
            }

            if (this.PCM.tipo === 'Programa') {
                const programa = await cadastrarPrograma({
                    nome: this.PCM.nome,
                    dtInicio: String(this.PCM.dtInicio).split(' ')[0],
                    gerente_id: this.PCM.gerentePCM_id,
                    status: 'Em andamento',
                })
                if (programa?.id) {
                    await servicePCM.atualizarPCM('programa_id', programa.id, response.id)
                }
            }
        },
        /** Compatibilidade: backend sem criação atômica. Cria o projeto e vincula à proposta. */
        async criarProjetoLegado(idPCM) {
            try {
                const projeto = await serviceProjetos.criarProjeto({
                    nome: this.PCM.nome,
                    dtInicio: String(this.PCM.dtInicio).split(' ')[0],
                    gerente_id: this.PCM.gerentePCM_id,
                    demandante_id: this.resolverDemandanteProjeto({ avisar: false }),
                    setor_id: this.PCM.setor_id,
                    usuario_id: this.idUsuario,
                    solicitante_id: this.PCM.solicitante_id,
                    status: this.PCM.nivel === 'Estratégico' ? 'Proposto' : 'Em andamento',
                })
                await servicePCM.atualizarPCM('projeto_id', projeto.id, idPCM)
                return projeto.id
            } catch (error) {
                this.handleApiError(error)
                this.toast.error(
                    `A proposta ${this.PCM.codigo || ''} foi criada, mas o projeto não pôde ser vinculado. Entre em contato com o suporte informando o código.`,
                )
                return null
            }
        },
        removerSetorEnvolvido(id) {
            this.PCM.setores_envolvidos = this.PCM.setores_envolvidos.filter(i => i.id !== id)
            var payload = this.PCM.setores_envolvidos.map(i => ({ setor_id: i.id, justificativa: i.justificativa ?? '' }));
            this.atualizarPCM('setores_envolvidos', payload);
        },
        adicionarSetorEnvolvido() {
            this.PCM.setores_envolvidos.push(this.novoSetorEnvolvido);
            var payload = this.PCM.setores_envolvidos.map(item => ({
                setor_id: item.id,
                justificativa: item.justificativa ?? ''
            }));
            this.atualizarPCM('setores_envolvidos', payload);
            this.novoSetorEnvolvido = null
        },
        payloadObjetivos() {
            if (!this.PCM.objetivos_estrategicos) {
                return this.objetivos.filter(item => item.ativo == true).map(i => ({ objetivo_id: i.id, justificativa: i.justificativa }));
            } else {
                return this.PCM.objetivos_estrategicos.filter(item => item.ativo == true).map(i => ({ objetivo_id: i.id, justificativa: i.justificativa }))
            }
        },
        listaObjetivosEstrategicos() {
            return this.PCM.objetivos_estrategicos ?? this.objetivos ?? []
        },
        objetivoEstaAtivo(item) {
            return item?.ativo == true || item?.ativo === 1 || item?.ativo === '1'
        },
        normalizarObjetivosParaPayload() {
            return this.listaObjetivosEstrategicos()
                .filter((item) => this.objetivoEstaAtivo(item))
                .map((item) => ({
                    objetivo_id: item.id,
                    nome: item.nome ?? '',
                    justificativa: item.justificativa ?? null,
                }))
                .sort((a, b) => a.objetivo_id - b.objetivo_id)
        },
        serializarObjetivosParaComparacao() {
            return JSON.stringify(this.normalizarObjetivosParaPayload())
        },
        syncObjetivosSnapshotBase() {
            this.objetivosSnapshotBase = this.serializarObjetivosParaComparacao()
        },
        onChangeObjetivoEstrategico(item) {
            if (this.bloquearEdicaoCampos && this.podeSolicitarEdicao) return
            const id = item?.id ?? item?.objetivo_id
            this.atualizarPCM(
                'objetivos',
                this.payloadObjetivos(),
                `objetivos_estrategicos.${id}`,
            )
        },
        selecionarImpactoViabilidade(item, concordo) {
            if (this.bloquearEdicaoCampos) return
            item.concordo = concordo
            item.impact_state = null
            item.impact_reason = ''
            item.impact_requires_confirmation = false
            this.atualizarPCM(
                'impacto_viabilidade',
                this.objetoSemIndiceDescricao(item),
                `impacto_viabilidade.${item.impactoViabilidade_id}`,
            )
        },
        abrirPedidoEdicaoObjetivos(propostaObjetivos = null) {
            if (!this.podeSolicitarEdicao) return
            const atual = this.objetivosSnapshotBasePedido
                ? JSON.parse(this.objetivosSnapshotBasePedido)
                : this.normalizarObjetivosParaPayload()
            const proposta = propostaObjetivos ?? this.normalizarObjetivosParaPayload()
            this.pedidoEdicao = {
                pcm_id: this.PCM.id,
                campo: 'objetivos_estrategicos',
                valor_atual: JSON.stringify(atual),
                novo_valor: JSON.stringify(proposta),
            }
        },
        solicitarEdicaoObjetivosComEstadoAtual() {
            if (!this.podeSolicitarEdicao || !this.temAlteracoesObjetivosPendentes) return
            this.abrirPedidoEdicaoObjetivos()
        },
        salvarPedidoObjetivos() {
            this.solicitarEdicaoObjetivosComEstadoAtual()
        },
        parseObjetivosPedido(valor) {
            if (!valor) return []
            try {
                const parsed = JSON.parse(valor)
                return Array.isArray(parsed) ? parsed : []
            } catch (_) {
                return []
            }
        },
        resumoPedidoObjetivos(pedido) {
            const anterior = this.parseObjetivosPedido(pedido?.valor_atual)
            const proposto = this.parseObjetivosPedido(pedido?.novo_valor)
            const nomeObjetivo = (item) => {
                if (item?.nome) return item.nome
                const encontrado = this.listaObjetivosEstrategicos().find((o) => o.id == item?.objetivo_id)
                return encontrado?.nome || `Objetivo #${item?.objetivo_id ?? '?'}`
            }
            const formatarLista = (lista) => {
                if (!lista.length) return '(nenhum objetivo selecionado)'
                return lista.map((item) => `- ${nomeObjetivo(item)}`).join('\n')
            }
            return {
                valorAntigo: formatarLista(anterior),
                novoValor: formatarLista(proposto),
            }
        },
        objetoSemIndiceDescricao(item) {
            if (item) {
                const { indice, descricao, ...resto } = item;
                indice
                descricao
                return [resto];
            }
        },

        async atualizarPCM(itemEditado, valor, aiFieldPath = itemEditado) {
            if (this.somenteLeitura) return
            this.$emit('ai-field-edited', aiFieldPath)
            if (this.PCM.id == 'criar') {
                return;
            }
            if (this.bloquearEdicaoCampos) return
            await servicePCM.atualizarPCM(itemEditado, valor, this.PCM.id)
            if (itemEditado == 'nome' && this.PCM.id_associacao) {
                await serviceProjetos.atualizarProjeto(this.PCM.id_associacao, { nome: valor })
            }
            if (itemEditado === 'gerentePCM_id' && this.PCM.tipo === 'Projeto' && this.PCM.id_associacao) {
                this.$emit('getProjeto')
            }
        },

        async getSetores() {
            try {
                const { usuarios, setores } = await consultarSetores();
                this.usuarios = usuarios;
                this.setores = setores;
            } catch (error) {
                this.handleApiError(error);
            }
        },
        async carregarCatalogosTenant() {
            try {
                const [objetivos, impactos] = await Promise.all([
                    servicePCM.listarObjetivosEstrategicos(),
                    servicePCM.listarImpactoViabilidade(),
                ]);

                this.objetivos = (Array.isArray(objetivos) ? objetivos : []).map((item) => ({
                    id: item.id,
                    nome: item.nome,
                    justificativa: null,
                    ativo: 0,
                }));

                this.impacto_viabilidade = (Array.isArray(impactos) ? impactos : []).map((item) => ({
                    impactoViabilidade_id: item.id,
                    concordo: null,
                    justificativa: '',
                    descricao: item.descricao,
                }));
            } catch (error) {
                this.handleApiError(error);
            }
        },
        async onCatalogoObjetivosAlterado() {
            try {
                const data = await servicePCM.listarObjetivosEstrategicos();
                const catalogo = Array.isArray(data) ? data : [];
                const ativosPorId = {};

                const fonteAtual = this.PCM.objetivos_estrategicos ?? this.objetivos ?? [];
                for (const item of fonteAtual) {
                    const id = item.id;
                    if (id == null) continue;
                    const ativo = item.ativo === true || item.ativo === 1 || item.ativo === '1' ? 1 : 0;
                    ativosPorId[id] = ativo;
                }

                const sincronizados = catalogo.map((item) => ({
                    id: item.id,
                    nome: item.nome,
                    justificativa: null,
                    ativo: ativosPorId[item.id] ?? 0,
                }));

                this.objetivos = sincronizados;
                if (this.PCM.objetivos_estrategicos) {
                    this.PCM.objetivos_estrategicos = sincronizados.map((item) => ({ ...item }));
                }
                this.syncObjetivosSnapshotBase();
            } catch (error) {
                this.handleApiError(error);
            }
        },
    },
};
</script>
<style scoped>
.pedido-mudanca-status {
    align-self: end;
}

@media screen and (min-width: 971px) {
    .pedido-mudanca-status {
        grid-column: 4;
    }
}

/* ——— Rascunho local ——— */
.pcm-rascunho-aviso {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem 1.25rem;
    margin: 1rem 1.5rem 0;
    padding: 0.85rem 1rem;
    border-radius: 8px;
    border: 1px solid var(--cor-alerta, #d97706);
    border-left-width: 4px;
    background: color-mix(in srgb, var(--cor-alerta, #d97706) 10%, transparent);
}

.pcm-rascunho-aviso__texto {
    flex: 1 1 18rem;
    line-height: 1.4;
}

.pcm-rascunho-aviso__acoes {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.pcm-doc-acoes__dica {
    opacity: 0.75;
    max-width: 28rem;
    text-align: right;
}

/* ——— Layout tipo documento oficial ——— */
.pcm-ai-field {
    position: relative;
    transition: box-shadow 0.2s ease, background-color 0.2s ease;
}

.pcm-ai-field--contested {
    padding: 0.35rem;
    border-radius: 6px;
    box-shadow: inset 3px 0 0 var(--cor-alerta, #d97706);
    background: color-mix(in srgb, var(--cor-alerta, #d97706) 9%, transparent);
}

.pcm-documento-wrapper {
    border-radius: 12px;
    /* max-width: 68rem; */
    margin: 0 auto 2rem;
    /* padding: 0 0.75rem; */
}

.pcm-documento {
    background: var(--cor-bg);
    border: 1px solid var(--cor-separador);
    box-shadow: 0 12px 32px var(--cor-cinza);
    border-radius: 2px;
}

.pcm-doc-cabecalho {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 1.25rem 1.5rem;
    align-items: center;
    padding: 1.35rem 1.75rem 1.1rem;
    background: var(--cor-cinza);
    border-bottom: 1px solid var(--cor-separador);
}

@media (max-width: 720px) {
    .pcm-doc-cabecalho {
        grid-template-columns: 1fr;
        text-align: center;
    }

    .pcm-doc-cabecalho__marca {
        justify-content: center;
    }

    .pcm-doc-cabecalho__meta {
        justify-content: center;
    }
}

.pcm-doc-cabecalho__marca {
    display: flex;
    align-items: center;
}

.pcm-doc-logo {
    display: block;
    height: 2.75rem;
    width: auto;
    max-width: 11rem;
    object-fit: contain;
}

.pcm-doc-cabecalho__texto {
    min-width: 0;
}

.pcm-doc-titulo-principal {
    margin: 0 0 0.35rem;
    font-size: 1.15rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--cor-fonte-forte);
    line-height: 1.35;
}

.pcm-doc-titulo-secundario {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--cor-fonte-fraca);
    letter-spacing: 0.02em;
    line-height: 1.45;
}

.pcm-doc-cabecalho__meta {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    align-items: flex-end;
    text-align: right;
}

.pcm-doc-cabecalho__meta--rascunho {
    align-self: center;
}

.pcm-doc-meta-bloco {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
}

.pcm-doc-meta-rotulo {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--cor-fonte-fraca);
    font-weight: 600;
}

.pcm-doc-meta-valor {
    font-size: 0.95rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--cor-fonte-forte);
}

.pcm-doc-meta-valor--status {
    font-size: 0.8125rem;
    font-weight: 500;
}

.pcm-doc-rascunho {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    padding: 0.35rem 0.65rem;
    border: 1px dashed var(--cor-separador);
    color: var(--cor-fonte-fraca);
    background: var(--cor-cinza);
}

.pcm-doc-traco-duplo {
    height: 4px;
    border-top: 2px solid var(--cor-fonte-forte);
    border-bottom: 1px solid var(--cor-fonte-forte);
    margin: 0 1.75rem;
    opacity: 0.35;
}

.pcm-doc-corpo {
    padding: 1.25rem 1.75rem 1.75rem;
    background: var(--cor-bg);
}

.pcm-doc-secao-principal {
    margin: 0 0 0.85rem;
    font-size: 0.9375rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--cor-fonte-forte);
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--cor-separador);
}

.pcm-doc-fieldset {
    border: 1px solid var(--cor-separador) !important;
    border-radius: 2px !important;
    background: var(--cor-cinza) !important;
    box-shadow: none;
}

.pcm-doc-fieldset :deep(h3) {
    font-size: 0.875rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--cor-fonte-forte);
    margin: 0 0 0.5rem;
    font-family: inherit;
}

.pcm-doc-fieldset :deep(label) {
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--cor-fonte-fraca);
}

.pcm-doc-fieldset :deep(input:not([type="checkbox"])),
.pcm-doc-fieldset :deep(select),
.pcm-doc-fieldset :deep(textarea) {
    border-color: var(--cor-separador) !important;
    border-radius: 2px !important;
    background: var(--cor-bg) !important;
    color: var(--cor-fonte);
}

.pcm-doc-fieldset :deep(.tabela) {
    border: 1px solid var(--cor-separador);
}

.pcm-doc-fieldset :deep(.tabela th) {
    background: var(--cor-cinza2) !important;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
    color: var(--cor-fonte);
    border-color: var(--cor-separador) !important;
}

.pcm-doc-fieldset :deep(.tabela td) {
    border-color: var(--cor-separador) !important;
    vertical-align: top;
}

.pcm-doc-acoes {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
    flex-wrap: wrap;
    margin-top: 1rem;
    padding-right: 0.75rem;
}

.pcm-doc-declaracao {
    margin-top: 0.75rem;
    padding: 0.85rem 1rem;
    width: 100%;
    border: 1px solid var(--cor-separador);
    border-left: 4px solid var(--cor-primaria);
    border-radius: 2px;
    background: var(--cor-cinza);
    font-size: 0.875rem;
    line-height: 1.55;
    color: var(--cor-fonte);
    text-align: justify;
    hyphens: auto;
}

input[type="checkbox"]:disabled {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border: 2px solid var(--cor-separador);
    border-radius: 3px;
    background: var(--cor-bg);
    cursor: default;
    position: relative;
    vertical-align: middle;
}

input[type="checkbox"]:disabled:checked {
    background: var(--cor-sucesso, #28a745);
    border-color: var(--cor-sucesso, #28a745);
}

input[type="checkbox"]:disabled:checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 5px;
    height: 9px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.bi-trash {
    color: var(--cor-erro);
    font-size: 16px;
    cursor: pointer;
}

.pcm-resultados-lista {
    display: grid;
    gap: 1rem;
}

.pcm-resultado-card {
    padding: 1rem;
    border: 1px solid var(--cor-separador);
    border-radius: 2px;
    background: var(--cor-bg);
}

.pcm-resultado-card__cabecalho {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 0.75rem;
    align-items: end;
}

.pcm-resultado-card__indice {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    margin-bottom: 0.35rem;
    border-radius: 50%;
    background: var(--cor-primaria);
    color: #fff;
    font-weight: 700;
}

.pcm-resultado-card__nome,
.pcm-resultado-card__problema {
    min-width: 0;
}

.pcm-resultado-card__problema {
    margin-top: 0.85rem;
}

.pcm-resultado-card__conteudo {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    margin-top: 0.85rem;
}

.pcm-resultado-card__rodape {
    display: grid;
    grid-template-columns: minmax(9rem, 0.8fr) minmax(9rem, 0.8fr) minmax(13rem, 1.4fr);
    gap: 1rem;
    align-items: start;
    margin-top: 0.85rem;
    padding-top: 0.85rem;
    border-top: 1px solid var(--cor-separador);
}

.pcm-resultado-card label {
    display: block;
    margin-bottom: 0.3rem;
}

.pcm-resultado-card input:not([type="checkbox"]),
.pcm-resultado-card textarea {
    width: 100%;
}

.pcm-resultado-card label.pcm-check-sem-custo {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    width: fit-content;
    margin: 0;
    font-size: 0.85rem;
    font-weight: normal;
    color: var(--cor-texto-secundario, #555);
    cursor: pointer;
}

.pcm-resultado-card .pcm-check-sem-custo input[type="checkbox"] {
    flex-shrink: 0;
    margin: 0;
}

/* O money3 renderiza um input sem atributo type, então não herda o estilo global de campo de texto. */
.pcm-input-custo {
    width: 100%;
    box-sizing: border-box;
    padding: 12px 16px;
    border: 1px solid var(--cor-separador);
    border-radius: 6px;
    background: var(--cor-bg);
    color: var(--cor-fonte);
    font: inherit;
    transition: background-color 0.25s, border 0.25s;
}

.pcm-input-custo:focus {
    outline: none;
    border-color: var(--cor-primaria);
}

.pcm-input-custo[readonly] {
    border-color: var(--cor-cinza);
}

.pcm-resultado-card__acoes {
    align-self: center;
}

.pcm-resultado-card__acoes button.chip.bg-erro {
    border: 0;
    background: var(--cor-erro);
    color: var(--cor-bg);
    cursor: pointer;
}

.pcm-resultado-card__acoes button.chip.bg-erro:hover {
    filter: brightness(0.94);
}

.pcm-resultados-acoes,
.pcm-objetivos-acoes {
    display: flex;
    gap: 0.75rem;
    justify-content: center;
    flex-wrap: wrap;
    align-items: center;
}

.pcm-resultados-acoes {
    margin-top: 1rem;
}

@media (max-width: 800px) {
    .pcm-resultado-card__cabecalho,
    .pcm-resultado-card__conteudo,
    .pcm-resultado-card__rodape {
        grid-template-columns: 1fr;
    }

    .pcm-resultado-card__indice {
        margin-bottom: 0;
    }

    .pcm-resultado-card__acoes {
        justify-self: start;
    }
}

.pcm-objetivos-acoes {
    margin-top: 0.75rem;
}

.dropzone {
    position: relative;
    border: 2px dashed var(--cor-separador);
    border-radius: 12px;
    padding: 28px;
    text-align: center;
    cursor: pointer;
    transition: .2s ease;
    color: var(--cor-fonte-fraca);
    background: var(--cor-cinza);
}

.dropzone * {
    pointer-events: none;
}

.dropzone.dragging {
    border-color: var(--cor-primaria);
    background: var(--cor-primaria-fraca);
    color: var(--cor-fonte-forte);
    transform: scale(1.01);
}

.lista-anexos {
    list-style: none;
    padding-left: 0;
    margin-top: 10px;
}

.lista-anexos li {
    display: flex;
    align-items: center;
    gap: .5rem;
    padding: 6px 0;
    border-bottom: 1px dashed var(--cor-separador);
}

.lista-anexos .nome {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.bi-pencil-square {
    cursor: pointer;
    color: var(--cor-fonte-fraca);
}

.objetivos {
    display: flex;
    flex-flow: column;

}

a {
    cursor: pointer;
}

input:disabled,
select:disabled,
textarea:disabled {
    opacity: 1;
    cursor: not-allowed;
}

.pcm-icone-pedido {
    margin-left: 0.35rem;
    cursor: pointer;
    font-size: 0.95rem;
    vertical-align: middle;
}

.pcm-doc-icone-pdf {
    margin-left: 0.5rem;
    cursor: pointer;
    font-size: 1.05rem;
    vertical-align: middle;
    color: var(--cor-erro);
}

.pcm-textarea {
    width: 100%;
    min-height: 72px;
    padding: 0.5rem;
    border: 1px solid var(--cor-separador);
    border-radius: 6px;
    resize: vertical;
    box-sizing: border-box;
}

.fonte-maior {
    font-size: 1.05rem;
    vertical-align: top;
}

.button {
    /* width: 7rem;
    padding: 5px !important;
    border: none !important;
    border-radius: 6px !important; */
    cursor: pointer;
    color: var(--cor-fonte);
    margin-inline: 0.5rem;
}

.reprovar {
    background-color: #e02130;
}

.aprovar {
    background-color: #429867;
}

.reprovar:disabled {
    background-color: #e0213171 !important;
    cursor: not-allowed !important;
}


.aprovar:disabled {
    background-color: #4298677e !important;
    cursor: not-allowed !important;
}

.aprovar:hover,
.reprovar:hover {
    transition: 50ms linear;
    transform: scale(1.1);
}

:global(.dark) .pcm-documento {
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
}

.pcm-campo-com-pedido {
    display: flex;
    align-items: flex-start;
    gap: 0.35rem;
}

.pcm-campo-com-pedido .pcm-textarea,
.pcm-campo-com-pedido input {
    flex: 1;
    min-width: 0;
}

.pcm-campo-custo {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.pcm-aviso-sem-custo {
    margin: 0;
    padding: 0.55rem 0.65rem;
    font-size: 0.88rem;
    line-height: 1.3;
    border-radius: 6px;
    background: color-mix(in srgb, var(--cor-separador, #eee) 70%, transparent);
    color: var(--cor-texto-secundario, #555);
}

.pcm-custo-vazio {
    display: block;
    padding: 0.5rem 0;
    color: var(--cor-texto-secundario, #888);
}

.pcm-celula-campo {
    position: relative;
    min-width: 0;
}

.pcm-celula-campo .pcm-textarea {
    padding-right: 2rem;
    box-sizing: border-box;
}

.pcm-celula-campo--custo {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}

.pcm-btn-pedido {
    position: absolute;
    top: 0.45rem;
    right: 0.35rem;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.6rem;
    height: 1.6rem;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: color-mix(in srgb, var(--cor-bg, #fff) 88%, transparent);
    color: var(--cor-texto-secundario, #666);
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.pcm-resultado-card:hover .pcm-btn-pedido,
.pcm-celula-campo:focus-within .pcm-btn-pedido,
.pcm-btn-pedido:focus-visible {
    opacity: 1;
}

.pcm-btn-pedido:hover,
.pcm-btn-pedido:focus-visible {
    background: var(--cor-separador, #eee);
    color: var(--cor-fonte, #222);
}
</style>